/**
 * <mv-loupe> — watchmaker's loupe over product gallery images.
 *
 * On fine-pointer, hover-capable screens (md and up), a brass-rimmed lens
 * follows the pointer over any `[data-loupe-src]` element and shows that area
 * of the image magnified by `data-zoom` (default 1.8). Touch devices keep the
 * existing tap-to-open fullscreen zoom only.
 *
 * Required markup:
 *   [data-loupe-src]  — hovered trigger; value is a high-resolution image URL.
 *                       Must contain the rendered <img>.
 *   [data-loupe-lens] — the lens element, absolutely positioned inside <mv-loupe>.
 */
class MvLoupe extends HTMLElement {
  #ac;
  #lens;
  #media;
  #active = null;
  #frame = 0;
  #point = null;

  connectedCallback() {
    this.#lens = this.querySelector('[data-loupe-lens]');
    if (!this.#lens) return;

    this.#media = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 768px)');
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('pointermove', (event) => this.#onMove(event), opts);
    this.addEventListener('pointerleave', () => this.#hide(), opts);
    this.addEventListener('pointerdown', () => this.#hide(), opts);
    window.addEventListener('scroll', () => this.#hide(), { ...opts, passive: true });
    this.#media.addEventListener('change', () => this.#hide(), opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#frame);
  }

  #onMove(event) {
    if (!this.#media.matches || event.pointerType !== 'mouse') return this.#hide();

    const trigger = event.target.closest?.('[data-loupe-src]');
    const img = trigger?.querySelector('img');
    if (!trigger || !img || !this.contains(trigger)) return this.#hide();

    if (this.#active !== trigger) {
      this.#active = trigger;
      this.#lens.style.backgroundImage = `url("${trigger.dataset.loupeSrc}")`;
    }

    this.#point = { x: event.clientX, y: event.clientY, img };
    if (!this.#frame) this.#frame = requestAnimationFrame(() => this.#render());
  }

  #render() {
    this.#frame = 0;
    if (!this.#point) return;
    const { x, y, img } = this.#point;

    const rect = img.getBoundingClientRect();
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) return this.#hide();

    const zoom = parseFloat(this.dataset.zoom) || 1.8;
    const naturalWidth = img.naturalWidth || rect.width;
    const naturalHeight = img.naturalHeight || rect.height;

    // Mirror object-fit: cover so the lens shows exactly what sits under the pointer.
    const scale = Math.max(rect.width / naturalWidth, rect.height / naturalHeight);
    const renderedWidth = naturalWidth * scale;
    const renderedHeight = naturalHeight * scale;
    const offsetX = (rect.width - renderedWidth) / 2;
    const offsetY = (rect.height - renderedHeight) / 2;

    const hostRect = this.getBoundingClientRect();
    const size = this.#lens.offsetWidth || 176;
    const radius = size / 2;
    const imageX = x - rect.left - offsetX;
    const imageY = y - rect.top - offsetY;

    const lens = this.#lens;
    lens.style.backgroundSize = `${renderedWidth * zoom}px ${renderedHeight * zoom}px`;
    lens.style.backgroundPosition = `${radius - imageX * zoom}px ${radius - imageY * zoom}px`;
    lens.style.transform = `translate3d(${x - hostRect.left - radius}px, ${y - hostRect.top - radius}px, 0)`;
    lens.hidden = false;
  }

  #hide() {
    this.#point = null;
    if (this.#lens) this.#lens.hidden = true;
  }
}

if (!customElements.get('mv-loupe')) {
  customElements.define('mv-loupe', MvLoupe);
}
